import { useCallback, ReactElement } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { get } from 'lodash';
import clsx from 'clsx';

import { aiExtractAgreement } from '@flow/libs/oneflow-client/ai-extract';
import agreementsReducer from '@app/reducers/entities/agreements';
import agreementTemplatesReducer from '@app/reducers/entities/agreement-templates';
import { SYSTEM_TAG_ID } from '@flow/libs/tags/constants';
import { redirectToDocument } from '@flow/libs/agreement/navigation-helpers';

import { CancelButton } from '@app/components/buttons';
import Button from '@app/components/button';
import Confirmable from '@app/components/confirmable';
import TemplateResults from '@app/components/template-results';
import TemplateSearch from '@app/components/template-search';
import Modal from '@app/components/modal';
import { StaticSystemImportLabel } from '@app/components/tags/tag/tag';
import { SupportLink } from '@app/components/support-link';
import { T } from '@app/components/translation';

import { useImportContractContext } from '@app/contexts/import-contract-props';

import { ExtractionLoadingState } from './extraction-loading-state';
import { ImportMethodToggle } from './import-method-toggle';
import { AiExtensionDisabled } from './ai-extension-disabled';
import { ImportFileUpload } from './import-file-upload';
import { useAiImportStatus } from './hooks/use-ai-import-status';
import { useDocumentPdfListener } from './hooks/use-document-pdf-listener';
import {
  shouldRenderLoader,
  isImportButtonDisabled,
  isStandardImport,
  isAiImport,
  getWorkspaceQuerySelector,
} from './utils';
import { modalKey } from './constants';

import style from './import-contract.module.scss';

export type Props = {
  children: ReactElement | ReactElement[];
};

export const ImportContract = ({ children }: Props) => {
  const {
    importMethod,
    uploadedPdfName,
    isAiImportOptionEnabled,
    extractionState,
    uploadInitiated,
    pdfIsReady,
    assetUploadErrorCode,
    templatesLength,
    removeContract,
    setUploadedPdfName,
    setUploadInitiated,
    setImportMethod,
    agreement,
    selectedUniqueKey,
    setExtractionState,
    defaultExtractMethod,
  } = useImportContractContext();
  const dispatch = useDispatch();
  const queryName = useSelector((state) =>
    getWorkspaceQuerySelector(state, {
      queryName: 'active-templates-with-import-tag',
    }),
  );
  const query = useSelector((state) =>
    agreementTemplatesReducer.getQuerySelector(state, {
      name: queryName,
    }),
  );
  const createState = useSelector((state) => agreementsReducer.getCreateSelector(state));
  const { clearAiImportSubscription, subscribeToAiImportEvents } = useAiImportStatus();
  const { resetPdfUploadStatus } = useDocumentPdfListener();

  const requestTemplates = useCallback(() => {
    dispatch(
      agreementTemplatesReducer.queryAgreementTemplates({
        name: queryName,
        pagination: {
          limit: 10000,
          offset: 0,
        },
        params: {
          visible: 1,
          tags: [
            SYSTEM_TAG_ID,
          ],
        },
      }),
    );
  }, [
    dispatch,
    queryName,
  ]);

  const extract = useCallback(
    (agreementId: number) => {
      if (!agreementId) {
        return;
      }
      setExtractionState({
        error: null,
        loading: true,
      });
      aiExtractAgreement(agreementId)
        .then(() => {
          const onSuccess = () => {
            redirectToDocument(agreementId);
          };
          const onFailure = () =>
            setExtractionState({
              loading: false,
              error: new Error('AI import failed'),
            });
          subscribeToAiImportEvents({
            agreementId,
            onSuccess,
            onFailure,
          });
        })
        .catch((error: Error) => {
          setExtractionState({
            loading: false,
            error,
          });
        });
    },
    [
      subscribeToAiImportEvents,
      setExtractionState,
    ],
  );

  const resetCreateState = useCallback(() => {
    resetPdfUploadStatus();
    dispatch(agreementsReducer.createAgreementReset());
  }, [
    dispatch,
    resetPdfUploadStatus,
  ]);

  const handleEnter = useCallback(() => undefined, []);

  const handleOpen = useCallback(() => {
    resetCreateState();
    requestTemplates();
  }, [
    resetCreateState,
    requestTemplates,
  ]);

  const handleClose = useCallback(() => {
    setImportMethod(defaultExtractMethod);
    setUploadInitiated(false);
    setUploadedPdfName(null);
    setExtractionState({
      loading: false,
      error: null,
    });
    clearAiImportSubscription();
    removeContract();
  }, [
    clearAiImportSubscription,
    defaultExtractMethod,
    removeContract,
    setExtractionState,
    setUploadInitiated,
    setUploadedPdfName,
    setImportMethod,
  ]);

  const isLoading = useCallback(
    () => get(query, 'loading'),
    [
      query,
    ],
  );
  const isSearching = useCallback(
    () => Boolean(get(query, 'params.q')),
    [
      query,
    ],
  );

  const getErrorMessage = useCallback(() => {
    const statusCode = get(createState.error, 'body.status_code');
    const statusCodeExtraction = get(extractionState.error, 'response.status');

    if (
      isAiImport(importMethod) &&
      (statusCodeExtraction === 504 || extractionState.error?.message === 'AI import failed')
    ) {
      return {
        headerText: (
          <T
            _str="{featureName} is currently unavailable."
            _comment="Error header in the import modal."
            featureName="AI Extract"
          />
        ),
        bodyText: (
          <T
            _str="Please try again later or add the details manually."
            _comment="Error body in the import modal."
          />
        ),
      };
    }

    if (statusCode === undefined) {
      return null;
    }

    return {
      headerText: (
        <T
          _str="A contract could not be created from this template."
          _comment="Error header in the template launcher modal."
        />
      ),
      bodyText: (
        <>
          <SupportLink />
          <span> </span>
          <T
            _str="Error code: {statusCode}"
            statusCode={statusCode}
            _comment="Error body text when not possible to create a contract."
          />
        </>
      ),
    };
  }, [
    createState.error,
    extractionState.error,
    importMethod,
  ]);

  const renderBody = useCallback(() => {
    if (extractionState.loading) {
      return <ExtractionLoadingState />;
    }

    return (
      <Modal.BodySections>
        <Modal.BodySection showWhenLoading>
          <ImportMethodToggle />
          <AiExtensionDisabled />
          <ImportFileUpload />
        </Modal.BodySection>
        <Modal.BodySection showWhenLoading>
          <div
            className={clsx(style.TemplateSearchContainer, {
              [style.ShowHeader]:
                isStandardImport(importMethod) && !uploadedPdfName && !templatesLength,
              [style.ShowHeaderWithResults]:
                isStandardImport(importMethod) && !uploadedPdfName && templatesLength,
              [style.Hide]: isAiImport(importMethod) || uploadedPdfName,
            })}
          >
            <div className={style.Heading}>
              <T
                _str="Or use a template with the tag {import}"
                _comment="Helper text to make it clear to the user what types of templates can be used to import documents"
                import={<StaticSystemImportLabel />}
              />
            </div>
            <TemplateSearch
              isDisabled={shouldRenderLoader(uploadInitiated, pdfIsReady, assetUploadErrorCode)}
              isInImportContractModal
              isLoading={isLoading()}
              isSearching={isSearching()}
            />
          </div>
        </Modal.BodySection>
        <Modal.BodySection>
          <div
            className={clsx(style.TemplateContainer, {
              [style.Hide]: isAiImport(importMethod) || uploadedPdfName,
            })}
          >
            <TemplateResults
              isDisabled={shouldRenderLoader(uploadInitiated, pdfIsReady, assetUploadErrorCode)}
              isInImportContractModal
              isSearching={isSearching()}
              selectedUniqueKey={selectedUniqueKey === -1 ? null : selectedUniqueKey}
              isImportModal
            />
          </div>
        </Modal.BodySection>
      </Modal.BodySections>
    );
  }, [
    extractionState.loading,
    importMethod,
    uploadedPdfName,
    isLoading,
    isSearching,
    uploadInitiated,
    pdfIsReady,
    assetUploadErrorCode,
    selectedUniqueKey,
    templatesLength,
  ]);

  // eslint-disable-next-line react/no-unused-prop-types
  const renderActions = useCallback(
    ({ closeConfirmation }: { closeConfirmation: () => void }) => (
      <>
        <CancelButton
          onClick={closeConfirmation}
          modalKey={modalKey}
        />
        {(isStandardImport(importMethod) ||
          (isAiImport(importMethod) && isAiImportOptionEnabled)) && (
          <Button
            customClass={clsx(style.ImportButton, {
              [style.Widen]: isAiImport(importMethod),
              [style.Shrink]: isStandardImport(importMethod),
            })}
            data-testid="confirm-button"
            kind="primary"
            onClick={() => {
              if (agreement.current?.id) {
                if (isAiImport(importMethod)) {
                  extract(agreement.current.id);
                  return;
                }
                redirectToDocument(agreement.current.id);
              }
            }}
            disabled={isImportButtonDisabled(
              uploadedPdfName,
              pdfIsReady,
              importMethod,
              extractionState,
            )}
            trackable={{
              name: 'Temp Import Document',
              props: {
                location: modalKey,
                'import type': isAiImport(importMethod) ? 'ai import' : 'manual',
              },
            }}
          >
            <span
              className={clsx(style.Text, {
                [style.Show]: isStandardImport(importMethod),
                [style.Hide]: isAiImport(importMethod),
              })}
            >
              <T
                _str="Import"
                _comment="Used as the button text in the import contract modal"
                noWrap
              />
            </span>
            <span
              className={clsx(style.Text, {
                [style.Show]: isAiImport(importMethod),
                [style.Hide]: isStandardImport(importMethod),
              })}
            >
              <T
                _str="Import with AI"
                _comment="Used as the button text in the import contract modal"
                noWrap
              />
            </span>
          </Button>
        )}
      </>
    ),
    [
      isAiImportOptionEnabled,
      importMethod,
      extract,
      uploadedPdfName,
      pdfIsReady,
      extractionState,
      agreement,
    ],
  );

  return (
    <Confirmable
      data-testid="import"
      body={renderBody()}
      error={createState.error || extractionState.error}
      actions={renderActions}
      header={
        <T
          _str="Import"
          _comment="Used as the modal title in the template launcher"
        />
      }
      modalKey={modalKey}
      onEnter={handleEnter}
      onClose={handleClose}
      onOpen={handleOpen}
      isLoading={isLoading()}
      customErrorMessage={getErrorMessage()}
      portalClassName={style.ImportContractPortal}
    >
      {children}
    </Confirmable>
  );
};
